'use client';

import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faEllipsisVertical,
  faPencil,
  faRoute,
} from '@fortawesome/pro-solid-svg-icons';
import { CircularProgress } from 'src/components/PulseUI/CircularProgress';
import { Dropdown } from 'src/components/PulseUI/Dropdown';
import { EmptyState } from 'src/components/PulseUI/EmptyState';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { PulseIconButton } from 'src/components/PulseUI/PulseIconButton';
import type { RouteStopResponse } from 'src/api/redux/routes';
import { DeleteRouteDialog } from 'src/components/Routes/components/DeleteRouteDialog';
import useRouteDetailLogic from 'src/hooks/routes/useRouteDetailLogic';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useSite } from 'src/context/providers/SiteProvider';
import {
  LogStopActivityDialog,
  RenameRouteDialog,
  RestartRouteDialog,
  RouteCompleteBanner,
  StartingLocationCard,
  StopCard,
} from './components';
import { ROUTE_MENU_CLASS_NAME } from './constants';

/**
 * RouteDetailPanel - Drawer view for a saved route
 *
 * Shows the route header stats, the starting point and a card per stop. Pure render component; logic lives in useRouteDetailLogic.
 */
const RouteDetailPanel: React.FC = () => {
  const {
    route,
    stops,
    isLoading,
    visitedCount,
    isRouteComplete,
    getStopState,
    getVisitedActivity,
    isStopsError,
    handleRetry,
    startingPoint,
    distanceMiles,
    isOptimizing,
    optimizeError,
    canOptimize,
    handleOptimize,
    handleRename,
    handleRestart,
    handleDelete,
    handleRemoveStop,
    handleStartStop,
    handleCancelStop,
    handleMarkVisited,
    handleEdit,
    handleClose,
    isRenaming,
    isDeleting,
  } = useRouteDetailLogic();
  const { dispatch: siteDispatch } = useSite();
  const { openDrawer } = useDrawer();

  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isRestartOpen, setIsRestartOpen] = useState(false);
  const [stopToLog, setStopToLog] = useState<RouteStopResponse | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const closeDeleteDialog = useCallback(() => {
    setIsDeleteOpen(false);
    setDeleteError(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    const error = await handleDelete();
    if (error) {
      setDeleteError(error);
      return;
    }
    closeDeleteDialog();
  }, [handleDelete, closeDeleteDialog]);

  const handleOpenInMaps = useCallback((stop: RouteStopResponse) => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}`,
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  const handleViewProfile = useCallback(
    (stop: RouteStopResponse) => {
      siteDispatch({
        type: 'SET_CURRENT_PROVIDER',
        payload: { id: stop.provider_id, type: stop.provider_type },
      });
      openDrawer('profile');
    },
    [siteDispatch, openDrawer]
  );

  const handleConfirmRestart = useCallback(() => {
    handleRestart();
    setIsRestartOpen(false);
  }, [handleRestart]);

  if (isLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center p-8"
        data-testid="route-detail-loading"
      >
        <CircularProgress size="md" color="primary" />
      </div>
    );
  }

  if (!route || isStopsError) {
    return (
      <div className="flex flex-col gap-2 p-4" data-testid="route-detail-error">
        <div>
          <PulseIconButton
            variant="plain"
            color="neutral"
            size="sm"
            icon={faArrowLeft}
            onClick={handleClose}
            aria-label="Back to routes"
            data-testid="route-detail-error-back-button"
          />
        </div>
        <EmptyState
          title={route ? 'Unable to load stops' : 'Unable to load route'}
          description={
            route
              ? 'The stops for this route could not be loaded.'
              : 'This route could not be loaded. It may have been deleted.'
          }
          size="sm"
          className="bg-transparent border-none"
        />
        <PulseButton
          variant="outline"
          onClick={handleRetry}
          className="rounded-full"
          data-testid="route-detail-retry-button"
        >
          Retry
        </PulseButton>
      </div>
    );
  }

  const menuOptions = [
    ...(canOptimize
      ? [
          {
            label: 'Optimize for One-way',
            onClick: () => handleOptimize(false),
          },
          {
            label: 'Optimize for Round-trip',
            onClick: () => handleOptimize(true),
          },
        ]
      : []),
    { label: 'Rename Route', onClick: () => setIsRenameOpen(true) },
    ...(visitedCount > 0
      ? [{ label: 'Restart Route', onClick: () => setIsRestartOpen(true) }]
      : []),
    { label: 'Delete Route', onClick: () => setIsDeleteOpen(true) },
  ];

  return (
    <div className="flex flex-col h-full" data-testid="route-detail-panel">
      {/* Header */}
      <div className="flex flex-col gap-2 px-4 py-4 bg-sky-600 text-white shrink-0">
        <div className="flex items-center justify-between">
          <PulseIconButton
            variant="plain"
            color="neutral"
            size="sm"
            icon={faArrowLeft}
            onClick={handleClose}
            aria-label="Back to routes"
            className="text-white"
            data-testid="route-detail-back-button"
          />
          <div className="flex items-center gap-1">
            <PulseIconButton
              variant="plain"
              color="neutral"
              size="sm"
              icon={faPencil}
              onClick={handleEdit}
              aria-label="Edit route stops"
              className="text-white"
              data-testid="route-detail-edit-button"
            />
            <Dropdown
              position="bottom-right"
              className={ROUTE_MENU_CLASS_NAME}
              ariaLabel="Route options"
              options={menuOptions}
              slots={{
                trigger: {
                  component: (
                    <PulseIconButton
                      variant="plain"
                      color="neutral"
                      size="sm"
                      icon={faEllipsisVertical}
                      aria-label="Route options"
                      className="text-white"
                      loading={isDeleting}
                      data-testid="route-detail-menu"
                    />
                  ),
                },
              }}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon icon={faRoute} />
          <h3 className="text-xl font-bold truncate" data-testid="route-detail-title">
            {route.label}
          </h3>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span data-testid="route-detail-stats">
            <strong>{stops.length}</strong>{' '}
            {stops.length === 1 ? 'Stop' : 'Stops'}
            {distanceMiles !== null && (
              <>
                {'  '}
                <strong>{distanceMiles.toFixed(1)}</strong>mi
              </>
            )}
          </span>
          <span data-testid="route-detail-visited">
            <strong>
              {visitedCount} / {stops.length}
            </strong>{' '}
            Visited
          </span>
        </div>
      </div>

      {isRouteComplete && <RouteCompleteBanner onRestart={() => setIsRestartOpen(true)} />}

      {optimizeError && (
        <p
          className="px-4 py-2 text-xs text-red-600 border-b border-neutral-200"
          role="alert"
          data-testid="route-detail-optimize-error"
        >
          {optimizeError}
        </p>
      )}

      {/* Stops */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isOptimizing ? (
          <div
            className="flex justify-center p-8"
            data-testid="route-detail-optimizing"
          >
            <CircularProgress size="md" color="primary" />
          </div>
        ) : (
          <div className="flex flex-col gap-1 p-4">
            <StartingLocationCard startingPoint={startingPoint} />
            {stops.length === 0 ? (
              <p className="text-sm text-neutral-500 italic">
                This route has no stops. Use Edit to add some.
              </p>
            ) : (
              stops.map((stop, index) => (
                <StopCard
                  key={stop.id}
                  stop={stop}
                  state={getStopState(stop)}
                  visitedActivity={getVisitedActivity(stop)}
                  isLast={index === stops.length - 1}
                  onGo={handleStartStop}
                  onCancel={handleCancelStop}
                  onViewProfile={handleViewProfile}
                  onLogActivity={setStopToLog}
                  onOpenInMaps={handleOpenInMaps}
                  onRemove={handleRemoveStop}
                />
              ))
            )}
          </div>
        )}
      </div>

      <DeleteRouteDialog
        isOpen={isDeleteOpen}
        routeLabel={route.label}
        isDeleting={isDeleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={closeDeleteDialog}
      />

      {isRenameOpen && (
        <RenameRouteDialog
          isOpen
          currentLabel={route.label}
          isSaving={isRenaming}
          onSave={handleRename}
          onClose={() => setIsRenameOpen(false)}
        />
      )}
      <RestartRouteDialog
        isOpen={isRestartOpen}
        onConfirm={handleConfirmRestart}
        onCancel={() => setIsRestartOpen(false)}
      />
      {stopToLog && (
        <LogStopActivityDialog
          stop={stopToLog}
          onLogged={handleMarkVisited}
          onClose={() => setStopToLog(null)}
        />
      )}
    </div>
  );
};

export default RouteDetailPanel;
